import { StatusInfo } from '../common/Status'
import {
    OrderStatus,
    OrderPaymentStatus,
    OrderDeliveryType,
    DisplayOrderStatus,
    type Order,
} from './types'
import { FulfillmentStatus } from '../fulfillment/types'

export function getOrderStatusInfo(status: OrderStatus): StatusInfo {
    const map: Record<OrderStatus, StatusInfo> = {
        PENDING: { text: 'Pendiente', class: 'secondary', actionText: '' },
        CONFIRMED: { text: 'Confirmada', class: 'info', actionText: 'Confirmar orden' },
        PROCESSING: { text: 'En proceso', class: 'warning', actionText: 'Procesar orden' },
        PROCESSED: { text: 'Procesada', class: 'primary', actionText: 'Orden procesada' },
        ON_HOLD: { text: 'En espera', class: 'secondary', actionText: 'Orden en espera' },
        COMPLETED: { text: 'Completada', class: 'success', actionText: 'Completar orden' },
        CANCELLED: { text: 'Cancelada', class: 'danger', actionText: 'Cancelar orden' },
        FAILED: { text: 'Fallida', class: 'danger', actionText: '' },
    }

    return map[status] ?? { text: String(status), class: 'secondary' }
}

export function getOrderPaymentStatusInfo(status: OrderPaymentStatus): StatusInfo {
    const map: Record<OrderPaymentStatus, StatusInfo> = {
        PENDING: { text: 'Pago pendiente', class: 'warning', actionText: '' },
        PARTIAL: { text: 'Pago parcial', class: 'info', actionText: '' },
        PAID: { text: 'Pago completo', class: 'success', actionText: '' },
        OVERPAID: { text: 'Pago excedido', class: 'info', actionText: '' },
        REFUNDED: { text: 'Reembolsado', class: 'success', actionText: '' },
        PARTIALLY_REFUNDED: { text: 'Parcialmente reembolsado', class: 'warning', actionText: '' },
    }

    return map[status] ?? { text: String(status), class: 'secondary' }
}

const DISPLAY_ORDER_STATUS_INFO: Record<DisplayOrderStatus, StatusInfo> = {
    [DisplayOrderStatus.PENDIENTES_DE_PAGO]: { text: 'Pendientes de pago', class: 'warning', actionText: '' },
    [DisplayOrderStatus.EN_PROCESO]: { text: 'En proceso', class: 'info', actionText: '' },
    [DisplayOrderStatus.LISTAS_PARA_ENVIAR]: { text: 'Listas para enviar', class: 'primary', actionText: '' },
    [DisplayOrderStatus.LISTAS_PARA_RETIRAR]: { text: 'Listas para retirar', class: 'primary', actionText: '' },
    [DisplayOrderStatus.ENVIADAS]: { text: 'Enviadas', class: 'warning', actionText: '' },
    [DisplayOrderStatus.ENTREGADAS]: { text: 'Entregadas', class: 'success', actionText: '' },
    [DisplayOrderStatus.EN_ESPERA]: { text: 'En espera', class: 'secondary', actionText: '' },
    [DisplayOrderStatus.CON_PROBLEMAS]: { text: 'Con problemas', class: 'danger', actionText: '' },
    [DisplayOrderStatus.CANCELADAS]: { text: 'Canceladas', class: 'danger', actionText: '' },
    [DisplayOrderStatus.REEMBOLSADAS]: { text: 'Reembolsadas', class: 'success', actionText: '' },
}

export function getDisplayOrderStatusInfo(displayStatus: DisplayOrderStatus): StatusInfo {
    return DISPLAY_ORDER_STATUS_INFO[displayStatus] ?? { text: String(displayStatus), class: 'secondary' }
}

/** Order-like minimal shape for computing display status */
type OrderForDisplayStatus = Pick<Order, 'status' | 'paymentStatus' | 'deliveryType'> & {
    fulfillmentStatus?: FulfillmentStatus | null
}

/**
 * Derives the display (pseudo) status for an order from real status, paymentStatus, fulfillmentStatus and deliveryType.
 * Evaluation order matters: Canceladas and Reembolsadas are mutually exclusive (Canceladas wins).
 */
export function getDisplayOrderStatus(order: OrderForDisplayStatus): DisplayOrderStatus {
    const status = order.status as OrderStatus
    const paymentStatus = order.paymentStatus as OrderPaymentStatus
    const fulfillmentStatus = order.fulfillmentStatus ?? FulfillmentStatus.PENDING
    const deliveryType = order.deliveryType

    if (status === OrderStatus.CANCELLED) return DisplayOrderStatus.CANCELADAS
    if (
        paymentStatus === OrderPaymentStatus.REFUNDED ||
        paymentStatus === OrderPaymentStatus.PARTIALLY_REFUNDED
    ) {
        return DisplayOrderStatus.REEMBOLSADAS
    }
    if (status === OrderStatus.FAILED) return DisplayOrderStatus.CON_PROBLEMAS
    if (status === OrderStatus.ON_HOLD) return DisplayOrderStatus.EN_ESPERA
    if (status === OrderStatus.COMPLETED || fulfillmentStatus === FulfillmentStatus.DELIVERED) {
        return DisplayOrderStatus.ENTREGADAS
    }
    if (fulfillmentStatus === FulfillmentStatus.SHIPPED) return DisplayOrderStatus.ENVIADAS
    if (
        status === OrderStatus.PROCESSED &&
        deliveryType === OrderDeliveryType.SHIPPING &&
        fulfillmentStatus === FulfillmentStatus.PENDING
    ) {
        return DisplayOrderStatus.LISTAS_PARA_ENVIAR
    }
    if (status === OrderStatus.PROCESSED && deliveryType === OrderDeliveryType.PICKUP) {
        return DisplayOrderStatus.LISTAS_PARA_RETIRAR
    }
    if (
        (status === OrderStatus.CONFIRMED || status === OrderStatus.PROCESSING) &&
        (paymentStatus === OrderPaymentStatus.PAID || paymentStatus === OrderPaymentStatus.OVERPAID)
    ) {
        return DisplayOrderStatus.EN_PROCESO
    }
    if (
        status === OrderStatus.PENDING &&
        (paymentStatus === OrderPaymentStatus.PENDING || paymentStatus === OrderPaymentStatus.PARTIAL)
    ) {
        return DisplayOrderStatus.PENDIENTES_DE_PAGO
    }

    return DisplayOrderStatus.PENDIENTES_DE_PAGO
}
